import { useMemo, useState } from "react";

import LoadingSpinner from "@/components/LoadingSpinner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useUser } from "@/context/user-context";
import { Workspace } from "@/types/workspaces";

interface DuplicateToWorkspaceModalProps<T> {
  open: boolean;
  object: T;
  objectType?: string;
  objectTitle?: string;
  setOpen: (open: boolean) => void;
  onDuplicate: (workspaceId: number, object: T) => void;
  isLoading: boolean;
}

export const DuplicateToWorkspaceModal = <T,>({
  open,
  object,
  objectType,
  objectTitle,
  setOpen,
  onDuplicate,
  isLoading,
}: DuplicateToWorkspaceModalProps<T>) => {
  const [selectedWorkspace, setSelectedWorkspace] = useState<{
    id: number;
    name: string;
  } | null>(null);

  const userContext = useUser();

  const workspacesList = useMemo(
    (): Workspace[] => [...userContext.workspaces].sort((a, b) => a.name.localeCompare(b.name)),
    [userContext.workspaces],
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSelectWorkspace = (workspace: any) => {
    setSelectedWorkspace({
      id: workspace.id,
      name: workspace.name,
    });
  };

  const handleDuplicate = () => {
    if (!selectedWorkspace?.id) {
      return;
    }

    onDuplicate(selectedWorkspace.id, object);
  };

  const renderOptions = () => (
    <DropdownMenu>
      <DropdownMenuTrigger className="w-full">
        {selectedWorkspace?.name || <span className="text-gray-500">Select a workspace...</span>}
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {workspacesList
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .sort((workspace: any) => (workspace.id === userContext.activeWorkspace?.id ? -1 : 1))
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .map((workspace: any) => (
            <DropdownMenuItem key={workspace.id} onSelect={() => handleSelectWorkspace(workspace)}>
              {workspace.name}
              {workspace.id === userContext.activeWorkspace?.id ? " (current)" : ""}
            </DropdownMenuItem>
          ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <Dialog onOpenChange={setOpen} open={open}>
      <DialogContent className="overflow-x-hidden">
        <DialogHeader>
          <DialogTitle className="text-md break-all font-semibold">
            Duplicate {objectType} "{objectTitle}"
          </DialogTitle>
        </DialogHeader>
        <div className="my-2 flex flex-col gap-2 overflow-y-auto transition-all duration-500 ease-in-out">
          <label className="mr-2 text-sm">Destination Workspace</label>
          <div className="w-full">{renderOptions()}</div>
        </div>
        <DialogFooter>
          <Button
            className="bg-transparent text-sm"
            onClick={() => setOpen(false)}
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDuplicate}
            disabled={isLoading || !selectedWorkspace}
            className="w-24 rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-700"
          >
            {isLoading ? <LoadingSpinner size={5} /> : "Duplicate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
